import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';

const API = appConfig.apis.contractApiEnv;
const baseUrl = API.address;

export class ApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
    }
}

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) {
        if (res.status === 204 || res.headers.get('content-length') === '0') return {};
        const text = await res.text();
        if (!text) return {};
        const json = JSON.parse(text);
        return json.data !== undefined ? json.data : json;
    }
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.errors?.[0]?.message || body.message || `API error: ${res.status}`);
};

const post = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'POST', headers: await getHeaders(), body: JSON.stringify(data) }));

const get = async (path: string) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { headers: await getHeaders() }));

export const contractApi = {
    upload: (fileName: string, fileContent: string, operationNumber: string, uploadedBy: string, files?: { fileName: string; fileContent: string; docType: string }[]) =>
        post('/contracts/upload', { fileName, fileContent, operationNumber, uploadedBy, files }),

    extract: (contractId: string) =>
        post(`/contracts/${contractId}/extract`, {}),

    sendFeedback: (contractId: string, feedbacks: { field: string; extractedValue: string }[]) =>
        post(`/contracts/${contractId}/extract/feedback`, { feedbacks }),

    retryExtraction: (contractId: string, previousFeedbacks?: { field: string; extractedValue: string }[]) =>
        post(`/contracts/${contractId}/extract/retry`, { previousFeedbacks }),

    confirm: (contractId: string, editedFields?: Record<string, string>, confirmedBy?: string, signersEnrichment?: { govTaxId: string; email?: string; phone?: string }[]) =>
        post(`/contracts/${contractId}/confirm`, { editedFields, confirmedBy, signersEnrichment }),

    sendToSignature: (contractId: string, providerId: string, deliveryChannels?: { whatsapp?: boolean; sms?: boolean }, reminderConfig?: { reminderDelay?: number; reminderFrequency?: number; expireAfter?: number }) =>
        post(`/contracts/${contractId}/send-to-signature`, { providerId, deliveryChannels, reminderConfig }),

    getList: (params?: { page?: number; size?: number }) =>
        get(`/contracts/page-list?page=${params?.page || 1}&size=${params?.size || 100}`),

    getDetail: (contractId: string) =>
        get(`/contracts/${contractId}`),

    validate: (contractId: string) =>
        get(`/contracts/${contractId}/validate`),
};
